/**
 * Source Worker orchestration.
 *
 * Every source is fetched in parallel. Each request resolves to a status object rather
 * than rejecting, so one dead or slow Worker can never reject the batch — a failed
 * source is simply dropped from the merge and reported in the source strip.
 *
 * Records are validated here too: a Worker that returns malformed data has its bad
 * records discarded rather than being allowed to break rendering downstream.
 */

const FETCH_TIMEOUT_MS = 15000;

/**
 * Worker endpoints come from build-time env vars so the same bundle can point at
 * local `wrangler dev` instances or production. See .env.example.
 * A source with no configured URL is simply not registered.
 */
export const SOURCES = [
  { id: 'devpost', label: 'Devpost', url: import.meta.env.VITE_WORKER_DEVPOST },
  { id: 'mlh', label: 'MLH', url: import.meta.env.VITE_WORKER_MLH },
  { id: 'hackclub', label: 'Hack Club', url: import.meta.env.VITE_WORKER_HACKCLUB },
].filter((source) => Boolean(source.url));

const VALID_FORMATS = new Set(['in-person', 'hybrid', 'online']);

/** Structural guard — anything failing this is dropped rather than rendered. */
export function isValidRecord(record) {
  if (!record || typeof record !== 'object') return false;
  if (typeof record.name !== 'string' || record.name.trim() === '') return false;
  if (typeof record.source_url !== 'string' || !/^https?:\/\//.test(record.source_url)) return false;
  if (!VALID_FORMATS.has(record.format)) return false;
  if (Number.isNaN(Date.parse(record.start_date))) return false;

  const latOk = record.lat == null || (Number.isFinite(record.lat) && Math.abs(record.lat) <= 90);
  const longOk = record.long == null || (Number.isFinite(record.long) && Math.abs(record.long) <= 180);
  if (!latOk || !longOk) return false;

  return true;
}

/** Normalize whatever a Worker returned into a clean record array. */
function extractRecords(payload) {
  if (!payload || !Array.isArray(payload.hackathons)) return [];
  return payload.hackathons
    .filter(isValidRecord)
    .map((record) => ({
      ...record,
      themes: Array.isArray(record.themes) ? record.themes : [],
      // Give every record a stable id even if a Worker omitted one.
      id: record.id || `${record.source_platform}:${record.source_url}`,
    }));
}

async function fetchSource(source, signal) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true });

  const startedAt = performance.now();
  try {
    const res = await fetch(source.url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const payload = await res.json();
    const records = extractRecords(payload);
    const rawCount = Array.isArray(payload.hackathons) ? payload.hackathons.length : 0;

    return {
      id: source.id,
      label: source.label,
      status: 'ok',
      records,
      count: records.length,
      // Surfaced in the UI so silently-dropped malformed records are still observable.
      droppedCount: Math.max(0, rawCount - records.length),
      cached: res.headers.get('X-HackFind-Cache') === 'HIT',
      fetchedAt: payload.fetched_at || null,
      meta: payload.meta || {},
      durationMs: Math.round(performance.now() - startedAt),
    };
  } catch (error) {
    return {
      id: source.id,
      label: source.label,
      status: 'error',
      records: [],
      count: 0,
      droppedCount: 0,
      error: error.name === 'AbortError' ? 'Timed out' : error.message,
      durationMs: Math.round(performance.now() - startedAt),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fire every source in parallel.
 *
 * `onSettled` fires per source the moment it lands, which is what lets the UI render
 * fast sources immediately instead of waiting on the slowest one. The returned
 * Promise.all resolves once every source has settled, for the overall "done" state.
 */
export function fetchAllSources({ onSettled, signal } = {}) {
  return Promise.all(
    SOURCES.map((source) =>
      fetchSource(source, signal).then((result) => {
        if (onSettled) onSettled(result);
        return result;
      }),
    ),
  );
}
